import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SALON_IMAGES } from "@/lib/assets";

const SALON_IMAGE = SALON_IMAGES.stylingStuehle;

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1fr_1.1fr]">
      <div className="flex items-center px-8 sm:px-16 lg:px-24 py-20">
        <div className="w-full max-w-sm rise">
          <Logo className="h-12 sm:h-14 mb-20" />
          <p className="label-xs mb-5">Salon-Zugang</p>
          <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight leading-[1.05] mb-4">
            Style DNA
          </h1>
          <p className="text-sm text-muted-foreground mb-14 leading-relaxed">
            Die persönliche Stilberatung für Ihre Kundinnen und Kunden — exklusiv im Salon Figaro Busch.
          </p>

          <form onSubmit={submit} className="space-y-6">
            <div>
              <label className="label-xs block mb-3">E-Mail</label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 rounded-sm border-input bg-white focus-visible:ring-1 focus-visible:ring-foreground"
                placeholder="salon@figarobusch.at"
              />
            </div>
            <div>
              <label className="label-xs block mb-3">Passwort</label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12 rounded-sm border-input bg-white focus-visible:ring-1 focus-visible:ring-foreground"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="w-full h-12 rounded-sm bg-foreground text-primary-foreground text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-foreground/90 hover:-translate-y-[1px] transition-transform duration-200"
            >
              {busy ? "Anmelden …" : "Anmelden"}
            </Button>
          </form>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden bg-secondary">
        <img src={SALON_IMAGE} alt="Salon" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-foreground/35" />
        <div className="absolute bottom-16 left-16 right-16">
          <p className="font-display text-3xl font-light text-white leading-snug max-w-md">
            „Der richtige Schnitt beginnt mit dem richtigen Blick."
          </p>
          <p className="mt-4 text-[11px] uppercase tracking-[0.25em] text-white/70">Figaro Busch — seit 1974</p>
        </div>
      </div>
    </div>
  );
}
